/* global $ */
import React, { useState, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';

function BulkUploadModal({ isOpen, onClose, onUploadSuccess }) {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const fileInputRef = useRef(null);

    // Sample data for template download
    const sampleData = [
        {
            company_name: "Sample Company 1",
            email: "company1@example.com",
            phone: "1234567890",
            alternate_phone: "1234567891",
            whatsapp_no: "1234567890",
            industry: "Technology",
            sub_industry: "Software Development",
            address_line1: "123 Main Street",
            address_line2: "Suite 100",
            country_id: 1,
            state_id: 1,
            city_id: 1,
            notes: "Potential client from referral",
            status: "New",
            source: "Referral",
            lead_owner: "550e8400-e29b-41d4-a716-446655440001"
        },
        {
            company_name: "Sample Company 2", 
            email: "company2@example.com",
            phone: "0987654321",
            alternate_phone: "0987654322",
            whatsapp_no: "0987654321",
            industry: "Manufacturing",
            sub_industry: "Automotive",
            address_line1: "456 Business Ave",
            address_line2: "",
            country_id: 1,
            state_id: 2,
            city_id: 5,
            notes: "Met at trade show",
            status: "Pitching",
            source: "Trade Show",
            lead_owner: "550e8400-e29b-41d4-a716-446655440002"
        }
    ];

    // Download sample Excel template
    const downloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet(sampleData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Leads Template");
        
        // Set column widths
        const colWidths = [
            { wch: 20 }, // company_name
            { wch: 25 }, // email
            { wch: 15 }, // phone
            { wch: 15 }, // whatsapp_no
            { wch: 15 }  // status
        ];
        ws['!cols'] = colWidths;
        
        XLSX.writeFile(wb, "leads_template.xlsx");
        
        Swal.fire({
            icon: 'success',
            title: 'Template Downloaded!',
            text: 'Please fill the template and upload it back.',
            timer: 2000,
            showConfirmButton: false
        });
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Validate file type
            const fileType = selectedFile.name.split('.').pop().toLowerCase();
            if (!['xlsx', 'xls', 'csv'].includes(fileType)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Type',
                    text: 'Please select an Excel file (.xlsx, .xls) or CSV file.'
                });
                return;
            }
            
            setFile(selectedFile);
            previewFile(selectedFile);
        }
    };

    // Preview file data
    const previewFile = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                
                // Show preview of first 5 rows
                setPreviewData(jsonData.slice(0, 5));
                setShowPreview(true);
            } catch (error) {
                console.error('Error reading file:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error Reading File',
                    text: 'Please check if the file is valid and try again.'
                });
            }
        };
        reader.readAsArrayBuffer(file);
    };

    // Upload file to backend
    const handleUpload = async () => {
        if (!file) {
            Swal.fire({
                icon: 'warning',
                title: 'No File Selected',
                text: 'Please select a file to upload.'
            });
            return;
        }

        setUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axiosInstance.post('/leads/bulk-upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Upload Successful!',
                    text: `${response.data.data.successCount} leads imported successfully. ${response.data.data.errorCount || 0} errors.`,
                    confirmButtonText: 'OK'
                });
                
                // Reset modal state
                setFile(null);
                setPreviewData([]);
                setShowPreview(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                
                // Call success callback and close modal
                onUploadSuccess();
                onClose();
            }
        } catch (error) {
            console.error('Upload error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: error.response?.data?.message || 'Failed to upload leads. Please try again.',
            });
        } finally {
            setUploading(false);
        }
    };

    // Reset modal
    const handleClose = () => {
        setFile(null);
        setPreviewData([]);
        setShowPreview(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fe fe-upload me-2"></i>
                            Bulk Upload Leads
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={handleClose}
                            disabled={uploading}
                        ></button>
                    </div>
                    
                    <div className="modal-body">
                        {/* Download Template Section */}
                        <div className="alert alert-info">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <i className="fe fe-info me-2"></i>
                                    <strong>Need a template?</strong> Download our sample Excel file to get started.
                                </div>
                                <button 
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={downloadTemplate}
                                >
                                    <i className="fe fe-download me-1"></i>
                                    Download Template
                                </button>
                            </div>
                        </div>

                        {/* File Upload Section */}
                        <div className="mb-4">
                            <label className="form-label">Select Excel File</label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="form-control"
                                accept=".xlsx,.xls,.csv"
                                onChange={handleFileSelect}
                                disabled={uploading}
                            />
                            <small className="form-text text-muted">
                                Supported formats: .xlsx, .xls, .csv (Max size: 10MB)
                            </small>
                        </div>

                        {/* File Info */}
                        {file && (
                            <div className="card mb-3">
                                <div className="card-body py-2">
                                    <div className="d-flex align-items-center">
                                        <i className="fe fe-file-text text-primary me-2"></i>
                                        <div className="flex-grow-1">
                                            <strong>{file.name}</strong>
                                            <small className="text-muted d-block">
                                                Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                                            </small>
                                        </div>
                                        <button 
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => {
                                                setFile(null);
                                                setPreviewData([]);
                                                setShowPreview(false);
                                                if (fileInputRef.current) {
                                                    fileInputRef.current.value = '';
                                                }
                                            }}
                                            disabled={uploading}
                                        >
                                            <i className="fe fe-x"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Preview Section */}
                        {showPreview && previewData.length > 0 && (
                            <div className="mb-3">
                                <h6 className="mb-2">
                                    <i className="fe fe-eye me-2"></i>
                                    Preview (First 5 rows)
                                </h6>
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Company Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>WhatsApp</th>
                                                <th>Industry</th>
                                                <th>Status</th>
                                                <th>Source</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewData.map((row, index) => (
                                                <tr key={index}>
                                                    <td>{row.company_name || 'N/A'}</td>
                                                    <td>{row.email || 'N/A'}</td>
                                                    <td>{row.phone || 'N/A'}</td>
                                                    <td>{row.whatsapp_no || 'N/A'}</td>
                                                    <td>{row.industry || 'N/A'}</td>
                                                    <td>
                                                        <span className="badge bg-secondary">
                                                            {row.status || 'New'}
                                                        </span>
                                                    </td>
                                                    <td>{row.source || 'Other'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <small className="text-muted">
                                    <i className="fe fe-info me-1"></i>
                                    Total rows to be processed: {previewData.length > 0 ? 'Loading...' : 0}
                                </small>
                            </div>
                        )}

                        {/* Instructions */}
                        <div className="alert alert-warning">
                            <h6 className="alert-heading">
                                <i className="fe fe-alert-triangle me-2"></i>
                                Important Instructions:
                            </h6>
                            <ul className="mb-0 small">
                                <li>Required fields: <strong>company_name, phone, whatsapp_no, industry, sub_industry, address_line1, country_id, state_id, city_id, status, source, lead_owner</strong></li>
                                <li>Optional fields: <strong>email, alternate_phone, address_line2, notes</strong></li>
                                <li>Valid status values: New, Pitching, Profile Shared, Secure Lead, Proposal Sent, Won, Lost</li>
                                <li>Valid source values: Website, Referral, Social Media, Cold Call, Email Campaign, Trade Show, Advertisement, Other</li>
                                <li>ID fields (country_id, state_id, city_id) must be positive numbers</li>
                                <li>lead_owner must be a valid UUID format</li>
                                <li>Phone numbers should contain only digits, spaces, hyphens, plus signs, and parentheses</li>
                                <li>Duplicate emails will be skipped (if email is provided)</li>
                                <li>Empty or invalid rows will be ignored</li>
                                <li>Maximum 1000 rows per upload</li>
                            </ul>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={handleClose}
                            disabled={uploading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={handleUpload}
                            disabled={!file || uploading}
                        >
                            {uploading ? (
                                <>
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <i className="fe fe-upload me-2"></i>
                                    Upload Leads
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BulkUploadModal;